import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { SsoTestResult } from "../hooks/useTestConnection";

export const EMAIL_ATTRIBUTE_DEFAULT =
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";

export interface SpMetadata {
  entityId: string;
  acsUrl: string;
  sloUrl?: string | null;
}

export interface SsoConfigFormProps {
  /** Page title displayed at the top */
  title: ReactNode;
  /** Initial value for display name field */
  initialDisplayName?: string;
  /** Initial value for entity descriptor URL field */
  initialEntityDescriptorUrl?: string;
  /** Initial value for email attribute field */
  initialEmailAttribute?: string;
  /** SP metadata for read-only fields */
  spMetadata?: SpMetadata | null;
  /** Whether the save action is in progress */
  isPending: boolean;
  /** General error message to display at the top */
  error?: string | null;
  /** Field-specific validation errors from the API */
  fieldErrors?: Record<string, string> | null;
  /** Called when user clicks Save with validated values */
  onSave: (values: {
    displayName: string;
    entityDescriptorUrl: string;
    emailAttribute: string;
  }) => void;
  /** Called when user clicks Back */
  onBack: () => void;
  /** SSO config data for certificate section (available after save) */
  ssoConfig?: {
    status: "TEST" | "ENABLED" | "DISABLED";
    idpName?: string | null;
    signingCert?: string | null;
    encryptionCert?: string | null;
  } | null;
  /** Whether the SSO config is currently loading/refetching */
  isConfigLoading?: boolean;
  /** Success message to display at the top of the form */
  successMessage?: string | null;
  /** Called when user clicks Enable SSO */
  onEnableSso?: () => void;
  /** Whether the enable SSO action is in progress */
  isEnabling?: boolean;
  /** Error message for the enable SSO action */
  enableError?: string | null;
  /** Error message when config refetch fails after save */
  configFetchError?: string | null;
  /** Callback to retry fetching the config */
  onRetryConfigFetch?: () => void;
}

interface UseSsoConfigFormParams {
  initialDisplayName: string;
  initialEntityDescriptorUrl: string;
  initialEmailAttribute: string;
  error?: string | null;
  fieldErrors?: Record<string, string> | null;
  onSave: (values: {
    displayName: string;
    entityDescriptorUrl: string;
    emailAttribute: string;
  }) => void;
}

export function useSsoConfigForm({
  initialDisplayName,
  initialEntityDescriptorUrl,
  initialEmailAttribute,
  error,
  fieldErrors,
  onSave,
}: UseSsoConfigFormParams) {
  const errorRef = useRef<HTMLDivElement>(null);

  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [entityDescriptorUrl, setEntityDescriptorUrl] = useState(
    initialEntityDescriptorUrl,
  );
  const [emailAttribute, setEmailAttribute] = useState(initialEmailAttribute);
  const [isCriticalDisplayName, setIsCriticalDisplayName] = useState(false);
  const [isCriticalUrl, setIsCriticalUrl] = useState(false);
  const [testSucceeded, setTestSucceeded] = useState(false);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  const isDirty =
    displayName.trim() !== initialDisplayName.trim() ||
    entityDescriptorUrl.trim() !== initialEntityDescriptorUrl.trim() ||
    emailAttribute.trim() !== initialEmailAttribute.trim();

  // Reset dismiss state when error changes so new errors always re-show the banner.
  useEffect(() => {
    setIsBannerDismissed(false);
  }, [error]);

  // Mark fields as critical when field errors arrive from the API.
  useEffect(() => {
    if (fieldErrors?.metadataUrl) {
      setIsCriticalUrl(true);
    }
    if (fieldErrors?.displayName) {
      setIsCriticalDisplayName(true);
    }
  }, [fieldErrors]);

  // Scroll to the error banner when it (re-)appears.
  useEffect(() => {
    if (error && !isBannerDismissed && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      errorRef.current.focus();
    }
  }, [error, isBannerDismissed]);

  const handleSave = () => {
    // Dismiss banner when re-submitting, but only if one is currently visible.
    if (error) setIsBannerDismissed(true);

    let hasError = false;

    if (!displayName.trim()) {
      setIsCriticalDisplayName(true);
      hasError = true;
    }
    if (!entityDescriptorUrl.trim()) {
      setIsCriticalUrl(true);
      hasError = true;
    }

    if (hasError) return;

    onSave({
      displayName: displayName.trim(),
      entityDescriptorUrl: entityDescriptorUrl.trim(),
      emailAttribute: emailAttribute.trim(),
    });
  };

  const handleTestResult = (result: SsoTestResult) => {
    if (result.success) {
      setTestSucceeded(true);
    }
  };

  const handleCopy = async (value: string) => {
    await navigator.clipboard.writeText(value);
  };

  // Auto-dismiss banner on input change, but only when a banner is currently
  // visible so the first Save attempt always shows errors immediately.
  const handleSetDisplayName = (value: string) => {
    setDisplayName(value);
    if (error) setIsBannerDismissed(true);
  };

  const handleSetEntityDescriptorUrl = (value: string) => {
    setEntityDescriptorUrl(value);
    if (error) setIsBannerDismissed(true);
  };

  const handleSetEmailAttribute = (value: string) => {
    setEmailAttribute(value);
    if (error) setIsBannerDismissed(true);
  };

  return {
    displayName,
    entityDescriptorUrl,
    emailAttribute,
    isCriticalDisplayName,
    setIsCriticalDisplayName,
    isCriticalUrl,
    setIsCriticalUrl,
    testSucceeded,
    isBannerDismissed,
    setIsBannerDismissed,
    isDirty,
    errorRef,
    handleSave,
    handleTestResult,
    handleCopy,
    handleSetDisplayName,
    handleSetEntityDescriptorUrl,
    handleSetEmailAttribute,
  };
}
